import { useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import { insightArticles } from '@/mocks/insights';
import useLightReveal from '@/hooks/useLightReveal';

export default function InsightDetailPage() {
  const { slug = '' } = useParams();
  const ref = useRef<HTMLElement>(null);
  useLightReveal(ref, slug);

  const index = insightArticles.findIndex((a) => a.slug === slug);

  if (index === -1) {
    return (
      <>
        <title>Article not found | AUREN Insights</title>
        <Navbar />
        <main className="bg-background-50 pt-14 min-h-[70vh]">
          <div className="wrap pt-24 md:pt-36 pb-24">
            <span className="label-xs text-foreground-600">Insights / Not found</span>
            <h1 className="mt-6 font-heading font-light uppercase leading-[1] tracking-[0.02em] text-foreground-950 text-[clamp(34px,5vw,64px)] max-w-[16ch]">This article isn&rsquo;t available.</h1>
            <Link to="/insights" className="group mt-10 inline-flex items-center gap-4 label-xs text-foreground-950 cursor-pointer whitespace-nowrap">
              Back to insights<span className="circle-arrow inv-dark"><i className="ri-arrow-right-line" /></span>
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const a = insightArticles[index];
  const next = insightArticles[(index + 1) % insightArticles.length];

  return (
    <>
      <title>{`${a.title} | AUREN Insights`}</title>
      <meta name="description" content={a.summary} />
      <Navbar />
      <main ref={ref} className="bg-background-50 pt-14">
        <article>
          <header className="relative">
            <div className="pointer-events-none absolute inset-x-0 top-0 h-[520px] bg-gradient-to-b from-accent-100/50 to-transparent" aria-hidden="true" />
            <div className="wrap relative pt-10 md:pt-14">
              <div className="flex flex-wrap items-center justify-between gap-4 border-b border-foreground-950/15 pb-4">
                <nav aria-label="Breadcrumb" className="label-xs text-foreground-600">
                  <Link to="/insights" className="hover:text-foreground-950 link-line pb-1 cursor-pointer">Insights</Link>
                  <span className="mx-2">/</span>
                  <span className="text-foreground-950">{a.category}</span>
                  <span className="mx-2">/</span>
                  <time>{a.date}</time>
                </nav>
                <Link to="/insights" className="label-xs text-foreground-950 link-line pb-1 cursor-pointer whitespace-nowrap">Back to insights &larr;</Link>
              </div>
              <h1 className="pt-14 md:pt-24 pb-12 md:pb-16 font-heading font-light uppercase leading-[1.02] tracking-[0.02em] text-foreground-950 text-[clamp(34px,5vw,72px)] max-w-[900px]" data-fade>{a.title}</h1>
            </div>
          </header>

          <div className="wrap">
            <div className="w-full aspect-[4/3] md:aspect-[16/9] overflow-hidden bg-background-200" data-clip>
              <img src={a.cover} alt={`${a.title} — editorial photograph`} title={a.title} className="w-full h-full object-cover object-center" />
            </div>
          </div>

          <div className="wrap py-20 md:py-28">
            <div className="max-w-[720px] mx-auto">
              <p className="font-heading font-normal uppercase tracking-[0.04em] leading-[1.4] text-[18px] md:text-[22px] text-foreground-950" data-fade>{a.intro}</p>
              {a.sections.map((s, i) => (
                <section key={s.heading} className="mt-14 md:mt-16" data-fade>
                  <h2 className="label-xs text-foreground-950 flex gap-4 border-t border-foreground-950/15 pt-4">
                    <span className="text-foreground-600">{String(i + 1).padStart(2, '0')}</span>{s.heading}
                  </h2>
                  {s.paragraphs.map((p) => (
                    <p key={p.slice(0, 24)} className="mt-6 text-[16px] md:text-[17px] leading-[1.75] text-secondary-900">{p}</p>
                  ))}
                  {i === 1 && (
                    <blockquote className="mt-12 font-heading font-light uppercase tracking-[0.03em] leading-[1.3] text-[22px] md:text-[28px] text-primary-500">
                      &ldquo;{a.quote}&rdquo;
                    </blockquote>
                  )}
                </section>
              ))}
            </div>
          </div>

          <div className="wrap pb-20 md:pb-28">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
              {a.gallery.map((g, i) => (
                <div key={g} className="w-full aspect-[4/3] overflow-hidden bg-background-200" data-fade>
                  <img src={g} alt={`${a.title} — supporting photograph ${i + 1}`} title={a.title} loading="lazy" className="w-full h-full object-cover object-center" />
                </div>
              ))}
            </div>
          </div>
        </article>

        <nav aria-label="Article navigation" className="border-t border-foreground-950/15">
          <div className="wrap py-14 md:py-20 flex flex-col md:flex-row md:items-end justify-between gap-10">
            <Link to={`/insights/${next.slug}`} className="group flex items-end justify-between gap-8 flex-1 max-w-[760px] cursor-pointer">
              <span>
                <span className="label-xs text-foreground-600">Next article &rarr;</span>
                <span className="block mt-3 font-heading font-light uppercase tracking-[0.02em] leading-[1.05] text-foreground-950 group-hover:text-primary-500 transition-colors text-[clamp(24px,3.4vw,44px)]">{next.title}</span>
              </span>
              <span className="circle-arrow inv-dark text-foreground-950 shrink-0"><i className="ri-arrow-right-line" /></span>
            </Link>
            <Link to="/insights" className="label-xs text-foreground-950 link-line pb-1 cursor-pointer whitespace-nowrap self-start md:self-end">Back to insights</Link>
          </div>
        </nav>
      </main>
      <Footer />
    </>
  );
}